import FeatureGates from '@atlaskit/feature-gate-js-client';
import Heading from '@atlaskit/heading';
import { Flex, Inline, Pressable, Stack, Text, xcss } from '@atlaskit/primitives';
import { useFireCrossFlowAnalyticsEvents } from '@atlassian/surface-analytics';
import { AdControls } from '@post-office/components--ad-controls';
import { type NextBestAction } from '@post-office/confluence-next-best-action';
import { useMessageInstanceEvent } from '@post-office/message-instance-event-frontend-client';
import { useCallback, useEffect, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl-next';

import AINotesIcon from '../assets/ai-notes-icon.svg';
import { useLoomAiMeetingNotesCrossFlow } from '../hooks/use-loom-ai-meeting-notes-cross-flow';

const whyAmISeeingThisModalContent = {
	title: (
		<FormattedMessage
			id="post-office.confluence-side-nav.next-best-action.loom-ai-meeting-notes-in-confluence.reasons-modal-title"
			description="Title of the reasons modal explaining why the loom cross-flow touchpoint is being shown"
			defaultMessage="Why am I seeing this?"
		/>
	),
	message: (
		<p>
			<FormattedMessage
				id="post-office.confluence-side-nav.next-best-action.loom-ai-meeting-notes-in-confluence.reasons-modal-explanation"
				description="Explanation as to why the loom cross-flow touchpoint is being shown"
				defaultMessage="We think that Loom will improve your team's productivity. The recommendation is based on:"
			/>
		</p>
	),
	reasons: [
		<FormattedMessage
			id="post-office.confluence-side-nav.next-best-action.loom-ai-meeting-notes-in-confluence.reasons-modal-bullet-one"
			description="First bullet point of the reasons modal for loom cross-flow touchpoint ad"
			defaultMessage="the size of your team"
		/>,
		<FormattedMessage
			id="post-office.confluence-side-nav.next-best-action.loom-ai-meeting-notes-in-confluence.reasons-modal-bullet-two"
			description="Second bullet point of the reasons modal for loom cross-flow touchpoint ad"
			defaultMessage="your existing Atlassian products"
		/>,
		<FormattedMessage
			id="post-office.confluence-side-nav.next-best-action.loom-ai-meeting-notes-in-confluence.reasons-modal-bullet-three"
			description="Third bullet point of the reasons modal for loom cross-flow touchpoint ad"
			defaultMessage="usage of a meeting notes template in Confluence"
		/>,
	],
};

interface LoomAiMeetingNotesBannerProps {
	/**
	 * Callback fired when the user dismisses the banner or clicks the CTA
	 */
	onSideNavClick: (nextBestActionType: NextBestAction) => string | void | null;

	/**
	 * Callback fired after user closes CFFE, to check if Loom was actually provisioned
	 */
	onCallback: (callbackKey: NextBestAction) => Promise<void>;
}

export const LoomAiMeetingNotesBanner = ({
	onSideNavClick,
	onCallback,
}: LoomAiMeetingNotesBannerProps) => {
	const intl = useIntl();

	const title = intl.formatMessage({
		id: 'post-office.confluence-side-nav.next-best-action.loom-ai-meeting-notes-in-confluence.header',
		defaultMessage: 'Automate your meeting notes',
		description: 'Header text for loom ai meeting notes in confluence banner',
	});

	const description = intl.formatMessage({
		id: 'post-office.confluence-side-nav.next-best-action.loom-ai-meeting-notes-in-confluence.body',
		defaultMessage:
			'Let Loom capture meeting notes, so you can get notes and action items instantly. ',
		description: 'Body (description) text for loom ai meeting notes in confluence banner',
	});

	const ctaText = intl.formatMessage({
		id: 'post-office.confluence-side-nav.next-best-action.loom-ai-meeting-notes-in-confluence.cta',
		defaultMessage: 'Try it free',
		description: 'Call to action text for loom ai meeting notes in confluence banner',
	});

	const imageAltText = intl.formatMessage({
		id: 'post-office.confluence-side-nav.next-best-action.loom-ai-meeting-notes-in-confluence.image-alt',
		defaultMessage: 'Notepad with sparkling background',
		description: 'Alternate text for picture of notepad with sparkles behind it',
	});

	const { messageInstanceClicked } = useMessageInstanceEvent();
	const { openCrossFlow, isLoading } = useLoomAiMeetingNotesCrossFlow({
		pollForProvisioningSuccess: () => onCallback('LOOM_AI_MEETING_NOTES'),
	});
	const { fireInteractedUIEvent, fireViewedUIEvent } = useFireCrossFlowAnalyticsEvents();

	/**
	 * We can assume if we reached here all other eligibility has been met for the experiment
	 * (check `filters/index.ts`) so it is safe to fire an exposure every time. This {@link render}
	 * function can also only ever be rendered if we have triggered it using the side nav context
	 * (which happens after the user clicks on meeting notes template inside confluence).
	 *
	 * We will use this value to auto-skip the message (without displaying any UI) if it is not in variation.
	 */
	const isExperimentVariation = useMemo(
		() =>
			FeatureGates.getExperimentValue(
				'loom_ai_meeting_notes_in_confluence_experiment',
				'cohort',
				'control',
				{
					fireExperimentExposure: true,
				},
			) !== 'control',
		[],
	);

	useEffect(() => {
		if (isExperimentVariation)
			fireViewedUIEvent(
				{
					action: 'viewed',
					actionSubject: 'card',
					actionSubjectId: 'confluenceLoomSideNavBanner',
				},
				{
					source: 'confluenceLoomSideNavBannerCard',
					recommendedProductIds: ['loom'],
				},
			);
	}, [fireViewedUIEvent, isExperimentVariation]);

	/**
	 * Handler for when user dismisses via ad-controls (Not interested)
	 */
	const handleNotInterested = useCallback(() => {
		void messageInstanceClicked({
			messageEventType: 'clicked',
			clickedElement: {
				clickIdentifier: 'dismiss',
				clickedElement: 'button',
				buttonType: 'secondary',
			},
			action: {
				actionType: 'dismiss',
			},
			additionalMetadataFromFrontend: {},
		});
		/**
		 * Move to next message in queue for side nav placement
		 */
		onSideNavClick('LOOM_AI_MEETING_NOTES');
	}, [messageInstanceClicked, onSideNavClick]);

	/**
	 * This is required to remove the message from the placement (show the "next message")
	 * because the ad controls remind me later does not handle this
	 */
	const handleRemindMeLater = useCallback(() => {
		/**
		 * Move to next message in queue for side nav placement
		 */
		onSideNavClick('LOOM_AI_MEETING_NOTES');
	}, [onSideNavClick]);

	/**
	 * Handler for when user clicks on the CTA.
	 */
	const handleCtaClicked = useCallback(() => {
		openCrossFlow();
		fireInteractedUIEvent(
			{
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'confluenceLoomSideNavBannerLearnMore',
			},
			{ source: 'confluenceLoomSideNavBannerCard', targetProductId: 'loom' },
		);

		/**
		 * Dismiss the message once the user has clicked on it
		 */
		void messageInstanceClicked({
			messageEventType: 'clicked',
			clickedElement: {
				clickIdentifier: 'dismiss',
				clickedElement: 'button',
				buttonType: 'primary',
			},
			action: {
				actionType: 'open',
			},
			additionalMetadataFromFrontend: {},
		});
		/**
		 * Move to next message in queue for side nav placement
		 */
		onSideNavClick('LOOM_AI_MEETING_NOTES');
	}, [messageInstanceClicked, fireInteractedUIEvent, openCrossFlow, onSideNavClick]);

	if (!isExperimentVariation) {
		/**
		 * Get this message out of the queue since we don't want to display it
		 */
		onSideNavClick('LOOM_AI_MEETING_NOTES');
		return null;
	}

	return (
		<Flex
			direction="column"
			xcss={xcss({
				borderRadius: 'radius.large',
				backgroundColor: 'color.background.accent.blue.subtlest',
				padding: 'space.150',
				boxShadow: 'elevation.shadow.raised',
			})}
		>
			<Inline
				xcss={xcss({
					justifyContent: 'space-between',
				})}
			>
				<img src={AINotesIcon} alt={imageAltText} role="presentation" width={56} height={56} />
				<AdControls
					showWhyAmISeeingThis
					showRemindMeLater
					whyAmISeeingThisModalContent={whyAmISeeingThisModalContent}
					onDismissed={handleNotInterested}
					onRemindMeLater={handleRemindMeLater}
					additionalAnalyticsAttributes={{
						source: 'confluenceLoomSideNavBannerCard',
					}}
				/>
			</Inline>
			<Stack alignBlock="center" space="space.150">
				<Stack space="space.100">
					<Heading size="small">{title}</Heading>
					<Text>{description}</Text>
				</Stack>
				<Pressable
					isDisabled={isLoading}
					onClick={handleCtaClicked}
					/* Outline buttons do not exist at time of implementation so have to make custom element */
					xcss={xcss({
						backgroundColor: 'color.background.neutral.subtle',
						border: '1px solid',
						borderColor: 'color.border',
						/* Does not exist as token (closest is 3px) */
						borderRadius: '4px',
						minHeight: 'space.300',
						width: 'fit-content',
						paddingInline: 'space.150',
					})}
				>
					<Text size="medium" weight="medium">
						{ctaText}
					</Text>
				</Pressable>
			</Stack>
		</Flex>
	);
};
